export type DocumentExportFormat =
  | 'docx'
  | 'pdf';

export interface DocumentExportBranding {
  logoDataUrl: string | null;
  headerText: string | null;
  footerText: string | null;

  showLogo: boolean;
  showHeader: boolean;
  showFooter: boolean;
  showPageNumbers: boolean;

  logoWidthMm: number;

  marginTopMm: number;
  marginRightMm: number;
  marginBottomMm: number;
  marginLeftMm: number;
}

export interface DocumentExportInput {
  title: string;
  content: string;
  creator?: string | null;
  subject?: string | null;

  branding?:
    | DocumentExportBranding
    | null;
}

export interface DocumentExportArtifact {
  format: DocumentExportFormat;
  fileName: string;
  mimeType: string;
  blob: Blob;
}

export interface DocumentExporter {
  readonly format: DocumentExportFormat;

  export(
    input: DocumentExportInput
  ): Promise<DocumentExportArtifact>;
}

export const DOCX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const PDF_MIME_TYPE =
  'application/pdf';

const htmlEntityMap:
  Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
  };

const decodeHtmlEntities = (
  value: string
) =>
  value.replace(
    /&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi,
    (match, entity: string) => {
      const normalized =
        entity.toLowerCase();

      if (
        normalized.startsWith(
          '#x'
        )
      ) {
        const code =
          Number.parseInt(
            normalized.slice(2),
            16
          );

        return Number.isFinite(code)
          ? String.fromCodePoint(code)
          : match;
      }

      if (
        normalized.startsWith('#')
      ) {
        const code =
          Number.parseInt(
            normalized.slice(1),
            10
          );

        return Number.isFinite(code)
          ? String.fromCodePoint(code)
          : match;
      }

      return (
        htmlEntityMap[normalized] ??
        match
      );
    }
  );

export const slugifyDocumentFileName = (
  value: string
) => {
  const slug =
    String(value ?? '')
      .normalize('NFD')
      .replace(
        /[\u0300-\u036f]/g,
        ''
      )
      .toLowerCase()
      .replace(
        /[^a-z0-9]+/g,
        '-'
      )
      .replace(
        /^-+|-+$/g,
        ''
      );

  return slug ||
    'documento-gerado';
};

const looksLikeHtml = (
  value: string
) =>
  /<\/?[a-z][^>]*>/i.test(
    value
  );

export const normalizeDocumentExportContent = (
  value: string
) => {
  let content =
    String(value ?? '')
      .replace(/\r\n?/g, '\n');

  if (
    looksLikeHtml(content)
  ) {
    content =
      content
        .replace(
          /<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi,
          ''
        )
        .replace(
          /<br\s*\/?>/gi,
          '\n'
        )
        .replace(
          /<li\b[^>]*>/gi,
          '• '
        )
        .replace(
          /<\/(p|div|h[1-6]|li|blockquote|tr)>/gi,
          '\n'
        )
        .replace(
          /<[^>]+>/g,
          ''
        );

    content =
      decodeHtmlEntities(
        content
      );
  }

  return content
    .replace(
      /[ \t]+$/gm,
      ''
    )
    .replace(
      /\n{4,}/g,
      '\n\n\n'
    )
    .trim();
};

export const splitDocumentExportParagraphs = (
  value: string
) => {
  const content =
    normalizeDocumentExportContent(
      value
    );

  if (!content) {
    return [];
  }

  const raw =
    content.split('\n');

  const result: string[] = [];
  let previousWasBlank =
    false;

  for (const line of raw) {
    const normalized =
      line.trimEnd();

    const isBlank =
      normalized.trim() === '';

    if (isBlank) {
      if (!previousWasBlank) {
        result.push('');
      }

      previousWasBlank = true;
      continue;
    }

    result.push(normalized);
    previousWasBlank = false;
  }

  while (
    result.length > 0 &&
    result[result.length - 1] === ''
  ) {
    result.pop();
  }

  return result;
};

export const createDocumentExportFileName = (
  title: string,
  extension: string
) =>
  `${slugifyDocumentFileName(
    title
  )}.${extension.replace(
    /^\./,
    ''
  )}`;

export const downloadDocumentExportArtifact = (
  artifact: DocumentExportArtifact
) => {
  const url =
    URL.createObjectURL(
      artifact.blob
    );

  const anchor =
    document.createElement('a');

  anchor.href = url;
  anchor.download =
    artifact.fileName;
  anchor.rel =
    'noopener noreferrer';

  document.body.appendChild(
    anchor
  );

  anchor.click();
  anchor.remove();

  window.setTimeout(
    () =>
      URL.revokeObjectURL(
        url
      ),
    1000
  );
};